// @givmo/fundraisers-module — a game's bonus pledges, read off the game's own public read (FCM-WEB35,
// D-FCM-77). The read is the game page's (`GET /campaigns/{id}`: its `scoreboard`, FCM-BE118, and its
// `pledges`, FCM-BE117 and FCM-BE128, with GFU-BE150's sentences and FCM-BE181's `created_at`), so the console
// says exactly what fans see: the same states, from the same fields, by the donate page's own rule
// (`apps/donate/app/lib/game-page.ts`, `bonusState`), plus the two ends only organizers see. The committed
// snapshot declares none of FCM-BE128's or FCM-BE181's keys, so each is read by hand off `unknown`
// (`api-client.md` § Additive RESPONSE fields), defensively. Nothing is recomputed: every figure is the
// server's, shown in whole dollars (§4a), and no money string is parsed.
import { centsToMoneyString, formatMoneyDisplay } from '@givmo/api-client';

/** Where a bonus pledge stands (the donate page's `BonusState`, plus what only its organizers see). */
export type BonusStateKind = 'open' | 'pending' | 'unlocked' | 'not_met' | 'no_result';

export interface BonusRow {
  offerId: string;
  /** Its own fundraiser under the game. */
  fundraiserId: string;
  /** The count it reads: the moment's own yes/no, or the game's scoreboard. */
  measureId: string;
  /** A moment its staff settle (`yes`), or a count reaching a number (`at_least`). */
  kind: 'moment' | 'count';
  /** The fans' card's sentence: its served condition with the house's closing period. */
  sentence: string;
  /** Its one amount ("$5 a fan"): a flat pledge at its fixed rate; null for any other. */
  amountCents: number | null;
  /** The charity its fundraiser pays, where the read names one. */
  recipientCharityId: string | null;
  state: BonusStateKind;
  /** Its uncollected pledges and what they would be charged: null where the game hides its figures. */
  pledges: number | null;
  pledgedCents: number | null;
  /** A moment whose yes/no still counts (its early end comes first, before its result). */
  counting: boolean;
  /** When it was added (FCM-BE181), for the tab's one order: the newest first, never by state. */
  createdAt: string | null;
  /** When its pledging closes at the latest: the game's end, which a stop leaves as it is. */
  windowClosesAt: string | null;
}

/** The game's own count (its scoreboard's team side, never the opponent's): what a count's bonus pledge reads. */
export interface GameCount {
  measureId: string;
  subject: string;
  unit: string;
  /** The count now, as the server states it ("3"); null before a first report or while withheld. */
  count: string | null;
}

type Loose = Record<string, unknown>;

const record = (value: unknown): Loose | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Loose) : null;
const list = (value: unknown): Loose[] =>
  Array.isArray(value) ? value.map(record).filter((item): item is Loose => item !== null) : [];
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
const int = (value: unknown): number | null =>
  typeof value === 'number' && Number.isSafeInteger(value) ? value : null;

function pledgesOf(campaign: unknown): Loose | null {
  return record(record(campaign)?.pledges);
}

/** The donate page's state rule (`bonusState`), with the two ends only organizers see. */
export function bonusStateOf(offer: Loose): BonusStateKind {
  const condition = text(offer.condition);
  if (condition === 'met') return 'unlocked';
  if (condition === 'not_met') return 'not_met';
  if (condition === 'voided') return 'no_result';
  if (offer.reached === true) return 'unlocked';
  return offer.status === 'open' && offer.window_open === true ? 'open' : 'pending';
}

const BONUS_CONDITIONS: Readonly<Record<string, BonusRow['kind']>> = {
  yes: 'moment',
  at_least: 'count',
};

/**
 * The game's bonus pledges: every offer with a condition the read serves (FCM-BE128's word for them) but the
 * featured fundraiser's, newest first (`created_at`), never by state, so the one just added sits at the top
 * and no act moves a row under the staffer's thumb. Without `created_at` (a backend before FCM-BE181) the
 * read's own order stands.
 */
export function bonusRows(campaign: unknown): BonusRow[] {
  const pledges = pledgesOf(campaign);
  if (!pledges) return [];
  const featured = text(pledges.featured_fundraiser_id);
  const measures = list(pledges.measures);
  const rows = list(pledges.offers).flatMap((offer): BonusRow[] => {
    const kind = BONUS_CONDITIONS[text(offer.condition_kind) ?? ''];
    const offerId = text(offer.offer_id);
    const fundraiserId = text(offer.fundraiser_id);
    const measureId = text(offer.measure_id);
    if (!kind || !offerId || !fundraiserId || !measureId || fundraiserId === featured) return [];
    const served = text(offer.condition_sentence);
    const measure = measures.find((m) => m.measure_id === measureId);
    const recipients = record(offer.recipients);
    const charities = Array.isArray(recipients?.charity_ids) ? recipients.charity_ids : [];
    return [
      {
        offerId,
        fundraiserId,
        measureId,
        kind,
        sentence: served ? `${served}.` : (text(offer.rule_sentence) ?? ''),
        amountCents:
          offer.amount_rule === 'flat' && offer.rate_policy === 'fixed'
            ? int(offer.suggested_rate_cents)
            : null,
        recipientCharityId: text(charities[0]),
        state: bonusStateOf(offer),
        pledges: int(offer.pledge_count),
        pledgedCents: int(offer.pledged_cents),
        counting: measure?.counting === true,
        createdAt: text(offer.created_at),
        windowClosesAt: text(offer.window_closes_at),
      },
    ];
  });
  if (rows.some((row) => row.createdAt === null)) return rows;
  return [...rows].sort((a, b) => Date.parse(b.createdAt!) - Date.parse(a.createdAt!));
}

/**
 * Whether a moment can take its result now (review-105's F4): its yes/no counts (the lock ends it early
 * first), the game is under way (its yes/no may have ended early already), or its window has closed with the
 * game. Before first pitch the server refuses both its result and its early end (`PLEDGE_RESULT_BEFORE_END`,
 * `PLEDGE_END_EARLY_REFUSED`), so the row offers neither answer then, only Stop pledging.
 */
export function canSettle(row: BonusRow, gameUnderWay: boolean, now: number = Date.now()): boolean {
  if (row.kind !== 'moment' || (row.state !== 'open' && row.state !== 'pending')) return false;
  if (row.counting || gameUnderWay) return true;
  return row.windowClosesAt !== null && Date.parse(row.windowClosesAt) <= now;
}

/** Whether the game has ended, by its read's `ends_at` (review-105's F5): after it the act refuses every new
 *  bonus pledge ("A pledge's end must still be to come"), so the tab offers none. */
export function gameEnded(campaign: unknown, now: number = Date.now()): boolean {
  const endsAt = text(record(campaign)?.ends_at);
  return endsAt !== null && Date.parse(endsAt) <= now;
}

/** The fans' card's sentence the preview composes (FCM-BE181's NAMED `condition_sentence` on
 *  `PledgeRuleSentencePreviewOut`, the server's `condition_sentence()`), read by hand: the committed snapshot
 *  declares no such key. Null while absent (a backend before FCM-BE181), so no preview draws. */
export function previewSentenceOf(preview: unknown): string | null {
  return text(record(preview)?.condition_sentence);
}

/** What the Layers tree says under a page that hosts one of the game's pledges: a bonus pledge's state, and
 *  every host's pledges and pledged figure (the tree's Raised is money in). */
export interface PledgeLine {
  state: BonusStateKind | null;
  pledges: number | null;
  pledgedCents: number | null;
  createdAt: string | null;
}

export function pledgeLinesOf(campaign: unknown): Map<string, PledgeLine> {
  const lines = new Map<string, PledgeLine>();
  for (const offer of list(pledgesOf(campaign)?.offers)) {
    const fundraiserId = text(offer.fundraiser_id);
    if (!fundraiserId || lines.has(fundraiserId)) continue;
    const bonus = BONUS_CONDITIONS[text(offer.condition_kind) ?? ''] !== undefined;
    lines.set(fundraiserId, {
      state: bonus ? bonusStateOf(offer) : null,
      pledges: int(offer.pledge_count),
      pledgedCents: int(offer.pledged_cents),
      createdAt: text(offer.created_at),
    });
  }
  return lines;
}

/**
 * The game's own count, its scoreboard's team side: a count the game owns, answering no other (the opponent's
 * side names the team's, `opponent_of_measure_id`), that still counts; read off the game's `scoreboard`
 * (FCM-BE118), the counts the game owns. Null on a campaign that is no game: the server's act refuses one.
 */
export function gameCountOf(campaign: unknown): GameCount | null {
  const read = record(campaign);
  const scoreboard = record(read?.scoreboard);
  const team = list(scoreboard?.measures).find(
    (m) =>
      m.fundraiser_id === read?.campaign_id &&
      m.kind === 'count' &&
      m.opponent_of_measure_id == null &&
      m.status !== 'voided',
  );
  const measureId = text(team?.measure_id);
  if (!team || !measureId) return null;
  return {
    measureId,
    subject: text(team.subject_name) ?? '',
    unit: text(team.unit_label) ?? '',
    count: text(team.count),
  };
}

/** Whether the read is a game's page: a campaign with a scoreboard of its own, the mark the server's act takes. */
export function isGameRead(campaign: unknown): boolean {
  return gameCountOf(campaign) !== null;
}

/** Whether the game's own count still counts (a count's bonus pledge can still be added on it). */
export function gameStillCounts(campaign: unknown, count: GameCount | null): boolean {
  if (!count) return false;
  const scoreboard = record(record(campaign)?.scoreboard);
  const team = list(scoreboard?.measures).find((m) => m.measure_id === count.measureId);
  return team?.counting === true;
}

/** The game's own charity: what its featured fundraiser pays (D-FCM-70's "the game's"). */
export function gameCharityOf(campaign: unknown): string | null {
  const featured = record(pledgesOf(campaign)?.featured_recipients);
  return Array.isArray(featured?.charity_ids) ? text(featured.charity_ids[0]) : null;
}

/** A unit's plural, as the server's sentence says it, for the units a scoreboard keeps ("run" → "runs"). */
export function unitsOf(unit: string): string {
  const word = unit.trim();
  if (!word) return '';
  if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
  if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
  return `${word}s`;
}

/**
 * Whole dollars, grouped, half-up, from integer CENTS (§4a: never a cent on a figure): the cents rounded to
 * the dollar by integer math on an integer field, then stated by the shared string presenters, never a float.
 * `null` for anything that is no whole number of cents.
 */
export function wholeDollars(cents: number | null | undefined): string | null {
  if (cents == null || !Number.isSafeInteger(cents) || cents < 0) return null;
  const rounded = Math.floor((cents + 50) / 100) * 100;
  const exact = centsToMoneyString(rounded);
  const shown = exact === null ? null : formatMoneyDisplay(exact, { cents: 'when-present' });
  return shown === null ? null : `$${shown}`;
}
